import { ControllerRenderProps } from "react-hook-form";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { addScreenSize, getScreenSize } from "@/sideEffects/api";
import React from "react";
import { useSelector } from "react-redux";
import { selectActiveFirm } from "@/store/appSelectors";
import { useTranslation } from "react-i18next";
import { AppSelect, Option } from "@/components/ui/app-select";
import { ScreenSizeQuickCreateForm, ScreenSizeQuickCreateFormType } from "./form/ScreenSizeQuickCreateForm";
import { showApiError, showSuccess } from "@/lib/toast";
import { useHasPermission } from "@/components/permission/PermissionChecker2";

export const ScreenSizeSelect: React.FC<{
  field: ControllerRenderProps<any>;
}> = ({ field }) => {
  const { t } = useTranslation();
  const activeFirm = useSelector(selectActiveFirm);
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryFn: () =>
      getScreenSize(
        {
          numberOfObjectsPerPage: 1000,
          pageNumber: 1,
        },
        {
          Firm_Id: String(activeFirm?.firmGuid),
        }
      ),
    queryKey: ["getScreenSize"],
  });

  const addScreenSizeMutation = useMutation({
    mutationFn: (values: ScreenSizeQuickCreateFormType) =>
      addScreenSize({
        name: values.name,
        width: Number(values.width),
        height: Number(values.height),
        firmGuid: String(activeFirm?.firmGuid),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["getScreenSize"],
      });
      showSuccess(t("common.addedSuccess"));
    },
    onError: showApiError,
  });

  const handleQuickCreate = async (values: ScreenSizeQuickCreateFormType) => {
    const result = await addScreenSizeMutation.mutateAsync(values);
    return {
      value: result.data.newDataId,
      label: values.name,
    };
  };

  const options: Option[] = (data?.data?.result || []).map(
    (r) =>
      ({
        value: r.intId,
        label: r.name,
      } satisfies Option)
  );

  const hasPermission = useHasPermission("ScreenSize", ["Add"]);

  return (
    <AppSelect
      value={field.value}
      onChange={field.onChange}
      options={options}
      isLoading={isLoading}
      placeholder={t("entities.screenSize")}
      label={t("entities.screenSize")}
      quickCreate={
        hasPermission
          ? {
              buttonText: t("common.addNewEntity", { entity: t("entities.screenSize") }),
              dialogTitle: t("common.addNewEntity", { entity: t("entities.screenSize") }),
              FormComponent: ScreenSizeQuickCreateForm,
              onSubmit: handleQuickCreate,
            }
          : undefined
      }
    />
  );
};
